import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getAppForHost, type AppKey } from "@/lib/hostname/appForHost";

// Per-host favicon, <title>, meta description, Open Graph, Twitter Card,
// and canonical URL. SPAs can't serve different static <head> per domain,
// so we mutate it at runtime. Idempotent — runs on every host/route change.

interface BrandMeta {
  title: string;
  description: string;
  origin: string;
  ogImage: string;
  siteName: string;
  twitterHandle?: string;
}

// Per-host favicons + Open Graph images now point at the boss-uploaded
// brand artwork in the public `email-assets` Cloud storage bucket. This
// keeps web, email, and share previews on the same single source of truth.
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const STORAGE_BASE = `${SUPABASE_URL}/storage/v1/object/public/email-assets`;
const brandLogo = (slug: string) => `${STORAGE_BASE}/${slug}_logo`;

const ICONS: Record<AppKey, string> = {
  wavlynq: brandLogo("wavlynq"),
  fryqnc: brandLogo("fryqnc"),
  pulsynq: brandLogo("pulsynq"),
  mindlynq: brandLogo("mindlynq"),
  ablelynq: brandLogo("ablelynq"),
  deafwav: brandLogo("deafwav"),
  fullynq: brandLogo("fullynq"),
  jadesynq: brandLogo("jadesynq"),
  openlynq: brandLogo("openlynq"),
  spiritsynq: brandLogo("spiritsynq"),
};

const BRAND: Record<AppKey, BrandMeta> = {
  wavlynq: {
    siteName: "Wavlynq",
    title: "Wavlynq — One signal. Nine communities.",
    description:
      "Wavlynq is the umbrella for nine culturally-grounded community apps — discovery, mentorship, and a crisis-aware companion, built for the people who use it. 18+.",
    origin: "https://www.wavlynq.com",
    ogImage: "https://www.wavlynq.com/og-image.png",
  },
  fryqnc: {
    siteName: "Fryqncy",
    title: "Fryqncy — LGBTQIA+ community, mentorship & connection.",
    description:
      "Fryqncy is the LGBTQIA+ home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.fryqnc.com",
    ogImage: "https://www.fryqnc.com/og-image.png",
  },
  pulsynq: {
    siteName: "Pulsynq",
    title: "Pulsynq — Black community, mentorship & connection.",
    description:
      "Pulsynq is the Black community home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.pulsynq.com",
    ogImage: "https://www.pulsynq.com/og-image.png",
  },
  mindlynq: {
    siteName: "MindLynq",
    title: "MindLynq — Neurodivergent community, mentorship & connection.",
    description:
      "MindLynq is the neurodivergent home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.mindlynq.com",
    ogImage: "https://www.mindlynq.com/og-image.png",
  },
  ablelynq: {
    siteName: "Ablelynq",
    title: "Ablelynq — Disability community, mentorship & connection.",
    description:
      "Ablelynq is the disability community home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.ablelynq.com",
    ogImage: "https://www.ablelynq.com/og-image.png",
  },
  deafwav: {
    siteName: "Deafwav",
    title: "Deafwav — Deaf & HoH community, mentorship & connection.",
    description:
      "Deafwav is the Deaf and Hard-of-Hearing home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.deafwav.com",
    ogImage: "https://www.deafwav.com/og-image.png",
  },
  fullynq: {
    siteName: "Fullynq",
    title: "Fullynq — Body-positive community, mentorship & connection.",
    description:
      "Fullynq is the body-positive home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.fullynq.com",
    ogImage: "https://www.fullynq.com/og-image.png",
  },
  jadesynq: {
    siteName: "Jadesynq",
    title: "Jadesynq — AAPI community, mentorship & connection.",
    description:
      "Jadesynq is the AAPI home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.jadesynq.com",
    ogImage: "https://www.jadesynq.com/og-image.png",
  },
  openlynq: {
    siteName: "Openlynq",
    title: "Openlynq — Polyamorous & ENM community, mentorship & connection.",
    description:
      "Openlynq is the polyamorous and ethically non-monogamous home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.openlynq.com",
    ogImage: "https://www.openlynq.com/og-image.png",
  },
  spiritsynq: {
    siteName: "Spiritsynq",
    title: "Spiritsynq — Faith & spirituality community, mentorship & connection.",
    description:
      "Spiritsynq is the faith and spirituality home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.spiritsynq.com",
    ogImage: "https://www.spiritsynq.com/og-image.png",
  },
};

// Per-route metadata overrides. Match by exact pathname or by prefix.
// Keeps titles ≤60 chars and descriptions 50–160 chars.
type RouteMeta = {
  title?: (siteName: string) => string;
  description?: string;
  ogType?: string;
};
const ROUTE_OVERRIDES: Array<{ test: (p: string) => boolean; meta: RouteMeta }> = [
  { test: (p) => p === "/pricing", meta: {
    title: (n) => `Pricing — ${n}`,
    description: "Compare Signal, Pulse, Surge, Spectrum, and Attuned tiers. Monthly or annual. 7-day free trial on every paid tier.",
  }},
  { test: (p) => p === "/a-la-carte", meta: {
    title: (n) => `À la carte upgrades — ${n}`,
    description: "Single-purchase boosts: Amplify, Priority Matching, Super Signals, Resonate, Incognito, and more. Buy what you need.",
  }},
  { test: (p) => p === "/mentors", meta: {
    title: (n) => `Verified mentors — ${n}`,
    description: "Browse background-checked mentors across the Wavlynq network. Book sessions, learn from your community.",
  }},
  { test: (p) => p === "/events" || p === "/events/", meta: {
    title: (n) => `Community events — ${n}`,
    description: "Find and host meetups, workshops, and gatherings across the Wavlynq community network. 18+.",
  }},
  { test: (p) => p.startsWith("/events/") && p.length > 8, meta: {
    title: (n) => `Event — ${n}`,
    description: "Event details, location, and check-in for community gatherings on the Wavlynq network.",
    ogType: "event",
  }},
  { test: (p) => p === "/store", meta: {
    title: (n) => `Store — ${n}`,
    description: "A curated multi-vendor marketplace for community-owned brands across the Wavlynq network.",
  }},
  { test: (p) => p.startsWith("/store/product/"), meta: {
    title: (n) => `Product — ${n} Store`,
    description: "Shop community-vetted products from independent vendors on the Wavlynq Store.",
    ogType: "product",
  }},
  { test: (p) => p.startsWith("/store/book/"), meta: {
    title: (n) => `Book — ${n} Store`,
    description: "Independent and community-published books on the Wavlynq Store.",
    ogType: "book",
  }},
  { test: (p) => p === "/safety", meta: {
    title: (n) => `Safety Center — ${n}`,
    description: "Emergency Button, crisis resources, KYC, trusted circle, and how Wavlynq keeps the community safe. 18+.",
  }},
  { test: (p) => p === "/support", meta: {
    title: (n) => `Help & FAQ — ${n}`,
    description: "Answers about accounts, billing, safety, mentors, and the Wavlynq community network. Ask RAELA or browse the FAQ.",
  }},
  { test: (p) => p === "/partner-access", meta: {
    title: (n) => `Partner access — ${n}`,
    description: "Apply as a creator, vendor, mentor, press, or agency partner of the Wavlynq community network.",
  }},
  { test: (p) => p === "/privacy", meta: {
    title: (n) => `Privacy Policy — ${n}`,
    description: "How Wavlynq collects, uses, and protects your data. CCPA and GDPR compliant.",
  }},
  { test: (p) => p === "/terms", meta: {
    title: (n) => `Terms of Service — ${n}`,
    description: "The agreement between you and Young's Digital Atelier LLC governing use of Wavlynq and its sibling apps.",
  }},
  { test: (p) => p === "/cookies", meta: {
    title: (n) => `Cookie Policy — ${n}`,
    description: "How Wavlynq uses cookies and similar technologies, and how to manage your preferences.",
  }},
  { test: (p) => p === "/ai-policy", meta: {
    title: (n) => `AI Policy — ${n}`,
    description: "How RAELA, Wavlynq's AI companion, is built, governed, trained, and kept safe.",
  }},
  { test: (p) => p === "/dmca", meta: {
    title: (n) => `DMCA — ${n}`,
    description: "Report copyright infringement on Wavlynq. Submit a takedown notice or counter-notice.",
  }},
];

const resolveRouteMeta = (pathname: string): RouteMeta => {
  for (const r of ROUTE_OVERRIDES) if (r.test(pathname)) return r.meta;
  return {};
};

/** Upsert a `<meta name="...">` or `<meta property="...">` tag. */
const setMeta = (key: "name" | "property", value: string, content: string) => {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${value}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(key, value);
    document.head.appendChild(tag);
  }
  if (tag.content !== content) tag.content = content;
};

const setLink = (rel: string, href: string, type?: string) => {
  const existing = document.head.querySelectorAll<HTMLLinkElement>(`link[rel="${rel}"]`);
  existing.forEach((el) => el.parentNode?.removeChild(el));
  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;
  if (type) link.type = type;
  document.head.appendChild(link);
};

const JSONLD_ID = "wavlynq-jsonld-brand";
const setBrandJsonLd = (meta: BrandMeta) => {
  const data = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: meta.siteName,
      url: meta.origin,
      logo: meta.ogImage,
      parentOrganization: { "@type": "Organization", name: "Young's Digital Atelier LLC" },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: meta.siteName,
      url: meta.origin,
    },
  ];
  let tag = document.getElementById(JSONLD_ID) as HTMLScriptElement | null;
  if (!tag) {
    tag = document.createElement("script");
    tag.id = JSONLD_ID;
    tag.type = "application/ld+json";
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
};

const BrandHead = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const app = getAppForHost();
    const brand = BRAND[app];
    const route = resolveRouteMeta(pathname);
    const iconHref = ICONS[app];
    const iconType = iconHref.endsWith(".svg") ? "image/svg+xml" : "image/png";
    const url = `${brand.origin}${pathname}${search}`;
    const title = route.title ? route.title(brand.siteName) : brand.title;
    const description = route.description ?? brand.description;
    const ogType = route.ogType ?? "website";

    if (document.title !== title) document.title = title;

    setMeta("name", "description", description);
    setLink("canonical", url);

    setMeta("property", "og:site_name", brand.siteName);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", brand.ogImage);

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", brand.ogImage);

    setLink("icon", iconHref, iconType);
    setLink("shortcut icon", iconHref, iconType);
    setLink("apple-touch-icon", iconHref);

    setBrandJsonLd(brand);
  }, [pathname, search]);

  return null;
};

export default BrandHead;
